import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, PartyPopper, ArrowUp, ArrowDown, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

interface OrderItem {
  id: string;
  text: string;
  image_url?: string;
}

interface OrderingProps {
  question: string;
  items: OrderItem[];
  correctOrder: string[];
  onAnswer: (order: string[], isCorrect: boolean) => void;
  disabled?: boolean;
}

const OrderingActivity = ({ question, items, correctOrder, onAnswer, disabled }: OrderingProps) => {
  // Shuffle initially
  const [order, setOrder] = useState<string[]>(() => {
    const ids = items.map(i => i.id);
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    return ids;
  });
  const [answered, setAnswered] = useState(false);

  const moveItem = (index: number, direction: "up" | "down") => {
    if (answered || disabled) return;
    const newOrder = [...order];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    setOrder(newOrder);
  };

  const handleSubmit = () => {
    const isCorrect = order.every((id, i) => id === correctOrder[i]);
    setAnswered(true);
    onAnswer(order, isCorrect);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-foreground text-center">{question}</h3>

      <div className="space-y-2 max-w-md mx-auto">
        {order.map((itemId, index) => {
          const item = items.find(i => i.id === itemId)!;
          const isCorrectPosition = answered && correctOrder[index] === itemId;
          const isWrongPosition = answered && correctOrder[index] !== itemId;

          return (
            <motion.div
              key={itemId}
              layout
              className={cn(
                "flex items-center gap-3 p-3 rounded-xl border-2 bg-background transition-all",
                !answered && "border-border",
                isCorrectPosition && "border-green-500 bg-green-50 dark:bg-green-950/30",
                isWrongPosition && "border-red-500 bg-red-50 dark:bg-red-950/30"
              )}
            >
              <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                {index + 1}
              </span>
              <GripVertical className="w-4 h-4 text-muted-foreground" />
              {item.image_url && <img src={item.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />}
              <span className="flex-1 font-medium text-sm">{item.text}</span>
              
              {!answered && (
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveItem(index, "up")}
                    disabled={index === 0}
                    className="p-1 rounded hover:bg-muted disabled:opacity-30"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveItem(index, "down")}
                    disabled={index === order.length - 1}
                    className="p-1 rounded hover:bg-muted disabled:opacity-30"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
              )}
              {isCorrectPosition && <CheckCircle2 className="w-5 h-5 text-green-600" />}
              {isWrongPosition && <XCircle className="w-5 h-5 text-red-500" />}
            </motion.div>
          );
        })}
      </div>

      {!answered && (
        <div className="flex justify-center">
          <Button onClick={handleSubmit} className="px-8 gap-2">تأكيد الترتيب</Button>
        </div>
      )}

      <AnimatePresence>
        {answered && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "text-center p-4 rounded-xl",
              order.every((id, i) => id === correctOrder[i]) ? "bg-green-50 dark:bg-green-950/30" : "bg-amber-50 dark:bg-amber-950/30"
            )}
          >
            {order.every((id, i) => id === correctOrder[i]) ? (
              <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-400">
                <PartyPopper className="w-5 h-5" />
                <span className="font-bold">ترتيب صحيح! ممتاز 🌟</span>
              </div>
            ) : (
              <span className="font-bold text-amber-700 dark:text-amber-400">
                الترتيب غير صحيح. حاول مرة أخرى! 💪
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrderingActivity;
